import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { useExpenditureLabels, useDecimalFormat } from '../../../contexts/ExpenditureLabelsContext';
import DataLookupField from '../../../components/DataLookupField';
import {
  Plus, Edit, Trash2, Save, Search, Download, X,
  FileText, Users, ChevronRight, ChevronDown, Bell,
  Lock, Eye, Pencil, Settings, Package
} from 'lucide-react';
import { SortableTableHeader, useSortState, sortData } from '../../../components/SortableTableHeader';

export function ItemRecipePanel() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // UOM state
  const [uomList, setUomList] = useState([]);
  const [uomSort, handleUomSort] = useSortState('name', 'asc');
  const [uomTotal, setUomTotal] = useState(0);
  const [uomSearch, setUomSearch] = useState('');
  const [uomPage, setUomPage] = useState(0);
  const [showAddUom, setShowAddUom] = useState(false);
  const [newUom, setNewUom] = useState({ uom_name: '', uom_description: '', exchange_value: 1.0 });
  const pageSize = 25;

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { fetchUom(); }, [uomPage]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sRes, uRes] = await Promise.all([
        api.get('/api/admin/item-recipe-settings'),
        api.get('/api/admin/uom', { params: { limit: pageSize, skip: 0 } })
      ]);
      setSettings(sRes.data.settings || { recipe_calculation: 'portion_selling_price' });
      setUomList(uRes.data.uom || []);
      setUomTotal(uRes.data.total || 0);
    } catch (err) { setSettings({ recipe_calculation: 'portion_selling_price' }); }
    setLoading(false);
  };

  const fetchUom = async () => {
    try {
      const res = await api.get('/api/admin/uom', { params: { search: uomSearch, limit: pageSize, skip: uomPage * pageSize } });
      setUomList(res.data.uom || []);
      setUomTotal(res.data.total || 0);
    } catch (err) { console.error(err); }
  };

  const handleSave = async () => {
    setSaving(true);
    try { await api.put('/api/admin/item-recipe-settings', settings); toast.success('Recipe settings saved'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const handleCreateUom = async () => {
    if (!newUom.uom_name) { toast.error('UOM Name is required'); return; }
    try {
      await api.post('/api/admin/uom', newUom);
      toast.success('UOM created');
      setNewUom({ uom_name: '', uom_description: '', exchange_value: 1.0 });
      setShowAddUom(false);
      fetchUom();
    } catch (err) { toast.error(err.response?.data?.detail || 'Create failed'); }
  };

  const handleDeleteUom = async (name) => {
    try { await api.delete(`/api/admin/uom/${name}`); toast.success('UOM deleted'); fetchUom(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const handleSearchUom = () => { setUomPage(0); fetchUom(); };
  const totalPages = Math.ceil(uomTotal / pageSize);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-4xl" data-testid="item-recipe-panel">
      <h1 className="text-2xl font-bold mb-1">Item Recipe</h1>
      <p className="text-lg font-medium text-muted-foreground mb-6">Recipe Settings</p>

      {/* Section 1: Recipe Calculation */}
      <div className="border rounded-lg p-6 mb-6" data-testid="ir-recipe-calc-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">1</span>
          Recipe Calculation
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Please select the default variable that you would like to calculate (the other staying constant) when changing Recipe calculations due to quantity, item, or other changes. Be assured that users can still change both Portion Selling Price and Cost Percentage manually.
        </p>
        <div className="space-y-3">
          {[{ v: 'portion_selling_price', l: 'Portion Selling Price' }, { v: 'cost_percentage', l: 'Cost Percentage' }].map(o => (
            <label key={o.v} className="flex items-center gap-3 cursor-pointer">
              <input type="radio" name="recipe_calculation" value={o.v}
                checked={(settings.recipe_calculation || 'portion_selling_price') === o.v}
                onChange={e => setSettings(p => ({ ...p, recipe_calculation: e.target.value }))}
                className="h-5 w-5 text-primary" data-testid={`ir-calc-${o.v}`} />
              <span className="text-sm">{o.l}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Section 2: Unit of Measurement Listing */}
      <div className="border rounded-lg p-6 mb-6" data-testid="ir-uom-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">2</span>
          Unit Of Measurement Listing
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          The grid below lists the existing Units Of Measurement in the system. You can create a custom Unit Of Measurement by clicking on the 'Add New' button.
        </p>
        <div className="flex items-center gap-2 mb-4">
          <Input value={uomSearch} onChange={e => setUomSearch(e.target.value)} placeholder="Search" className="w-36" data-testid="ir-uom-search" />
          <Button size="sm" onClick={handleSearchUom}><Search className="h-3 w-3 mr-1" /> Search</Button>
          <Button size="sm" variant="outline" onClick={() => { setUomSearch(''); setUomPage(0); setTimeout(fetchUom, 100); }}><X className="h-3 w-3 mr-1" /> Clear</Button>
          <div className="flex-1" />
          <span className="text-xs font-medium">Total Record: {uomTotal}</span>
          <Button size="sm" variant="outline" onClick={() => setShowAddUom(true)} data-testid="ir-add-uom-btn">+ Add New</Button>
        </div>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <SortableTableHeader label="UOM Name" field="name" sortState={uomSort} onSort={handleUomSort} />
                <SortableTableHeader label="UOM Description" field="description" sortState={uomSort} onSort={handleUomSort} />
                <SortableTableHeader label="Exchange Value" field="exchange_value" sortState={uomSort} onSort={handleUomSort} className="text-right" />
                <SortableTableHeader label="System Unit" field="is_system_unit" sortState={uomSort} onSort={handleUomSort} className="text-center" />
                <SortableTableHeader label="Active" field="active" sortState={uomSort} onSort={handleUomSort} className="text-center" />
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {uomList.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground text-xs">No UOM records found</td></tr>
              ) : sortData(uomList, uomSort.field, uomSort.dir).map(u => (
                <tr key={u.uom_name} className="border-t hover:bg-accent/50">
                  <td className="px-3 py-2">{u.uom_name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{u.uom_description}</td>
                  <td className="px-3 py-2 text-right font-mono">{(u.exchange_value || 1).toFixed(5)}</td>
                  <td className="px-3 py-2 text-center">{u.system_unit ? 'Yes' : 'No'}</td>
                  <td className="px-3 py-2 text-center">{u.is_active !== false ? 'Yes' : 'No'}</td>
                  <td className="px-2">
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeleteUom(u.uom_name)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-3">
            <div className="flex gap-1">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => (
                <button key={i} onClick={() => setUomPage(i)}
                  className={`w-7 h-7 rounded text-xs ${uomPage === i ? 'bg-primary text-white' : 'bg-muted hover:bg-accent'}`}>{i + 1}</button>
              ))}
              {totalPages > 5 && <span className="text-xs text-muted-foreground px-1">...</span>}
            </div>
            <span className="text-xs text-muted-foreground">You are viewing page {uomPage + 1}/{totalPages}</span>
          </div>
        )}
        {/* Add UOM Dialog */}
        <Dialog open={showAddUom} onOpenChange={setShowAddUom}>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Unit of Measurement</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs">UOM Name *</Label>
                <Input value={newUom.uom_name} onChange={e => setNewUom(p => ({ ...p, uom_name: e.target.value.toUpperCase() }))} data-testid="ir-new-uom-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">UOM Description</Label>
                <Input value={newUom.uom_description} onChange={e => setNewUom(p => ({ ...p, uom_description: e.target.value }))} data-testid="ir-new-uom-desc" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Exchange Value</Label>
                <Input type="number" step="0.00001" value={newUom.exchange_value} onChange={e => setNewUom(p => ({ ...p, exchange_value: parseFloat(e.target.value) || 1 }))} data-testid="ir-new-uom-exchange" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddUom(false)}>Cancel</Button>
              <Button onClick={handleCreateUom}><Plus className="h-4 w-4 mr-1" /> Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} data-testid="item-recipe-save-btn">
          <Save className="h-4 w-4 mr-1" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
